/**
 * Canonical voice/session settings loaded from `public/AI/voice/settings.json`.
 *
 * The same file is compiled into Unity's generated AI catalog. Keep values out
 * of this module: it owns validation, device selection, and access only, so a
 * tuning change remains a one-file edit.
 */

import { publicUrl } from "@core/publicUrl";

export type VoiceMicrophoneProfileId =
  | "near_field_microphone"
  | "far_field_microphone";
export type VoiceNoiseReduction = "far_field" | "near_field";
export type VoiceReasoningEffort =
  | "none"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh";

export interface VoiceMicrophoneProfile {
  readonly turnDetectionThreshold: number;
  readonly noiseReduction: VoiceNoiseReduction;
}

export interface VoiceReasoningPolicy {
  readonly default: VoiceReasoningEffort;
  readonly roles: Readonly<Record<string, VoiceReasoningEffort>>;
}

export interface VoiceSettings {
  readonly version: 1;
  readonly devices: Readonly<
    Record<VoiceMicrophoneProfileId, VoiceMicrophoneProfile>
  >;
  readonly turnDetection: {
    readonly prefixPaddingMs: number;
    readonly silenceDurationMs: number;
    readonly interruptResponse: boolean;
    readonly idleTimeoutMs: number | null;
  };
  readonly transcription: {
    readonly model: string;
    readonly language: string;
  };
  readonly realtime: {
    readonly model: string;
    readonly parallelToolCalls: boolean;
  };
  readonly reasoningEffort: {
    readonly web: VoiceReasoningPolicy;
    readonly unity: VoiceReasoningPolicy;
  };
  readonly timing: {
    readonly scriptedStepPostSpeechWaitMs: number;
    readonly webScriptedStepFallbackPostSpeechWaitMs: number;
    readonly responseCompletionQuietMs: {
      readonly web: {
        readonly afterOutputBufferStoppedMs: number;
        readonly withoutOutputBufferEventsMs: number;
      };
      readonly unity: number;
    };
    readonly unityLocalAudioActivity: {
      readonly rmsThreshold: number;
      readonly agentSpeakingDrainCeilingMs: number;
      readonly userSpeakingHangoverMs: number;
    };
    readonly tapToListenNoSpeechTimeoutMs: number;
    readonly headsetPlaybackLeadMs: number;
  };
}

const K_SETTINGS_PATH = publicUrl("AI/voice/settings.json");
const K_MICROPHONE_PROFILE_IDS: ReadonlyArray<VoiceMicrophoneProfileId> = [
  "near_field_microphone",
  "far_field_microphone",
];
const K_REASONING_EFFORTS: ReadonlyArray<VoiceReasoningEffort> = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
];

let s_loadTask: Promise<VoiceSettings> | null = null;
let s_settings: VoiceSettings | null = null;

/** Loads and validates the required settings before Spark connects. */
export function ensureVoiceSettingsLoadedAsync(force = false): Promise<VoiceSettings> {
  if (force) {
    s_loadTask = null;
    s_settings = null;
  }
  return (s_loadTask ??= loadVoiceSettingsAsync());
}

/** Synchronous read for connection/session hot paths after startup. */
export function getVoiceSettings(): VoiceSettings {
  if (!s_settings) {
    throw new Error(
      "[voiceSettings] settings are not loaded; await ensureVoiceSettingsLoadedAsync() before starting Spark",
    );
  }
  return s_settings;
}

/** Web microphones use the far-field profile on every browser platform. */
export function getVoiceMicrophoneProfile(
  profileId: VoiceMicrophoneProfileId = "far_field_microphone",
): VoiceMicrophoneProfile {
  return getVoiceSettings().devices[profileId];
}

export function parseVoiceSettings(raw: unknown): VoiceSettings {
  const root = asRecord(raw, "$");
  const version = asNumber(root.version, "$.version");
  if (version !== 1) throw new Error(`$.version must be 1; received ${String(version)}`);

  const devicesRaw = asRecord(root.devices, "$.devices");
  const devices = Object.fromEntries(
    K_MICROPHONE_PROFILE_IDS.map((profileId) => {
      const path = `$.devices.${profileId}`;
      const profile = asRecord(devicesRaw[profileId], path);
      const threshold = asNumber(profile.turnDetectionThreshold, `${path}.turnDetectionThreshold`);
      if (threshold < 0 || threshold > 1) {
        throw new Error(`${path}.turnDetectionThreshold must be between 0 and 1`);
      }
      const noiseReduction = asString(profile.noiseReduction, `${path}.noiseReduction`);
      if (noiseReduction !== "far_field" && noiseReduction !== "near_field") {
        throw new Error(`${path}.noiseReduction must be "far_field" or "near_field"`);
      }
      return [
        profileId,
        { turnDetectionThreshold: threshold, noiseReduction },
      ] as const;
    }),
  ) as Record<VoiceMicrophoneProfileId, VoiceMicrophoneProfile>;

  const turnDetection = asRecord(root.turnDetection, "$.turnDetection");
  const transcription = asRecord(root.transcription, "$.transcription");
  const realtime = asRecord(root.realtime, "$.realtime");
  const reasoning = asRecord(root.reasoningEffort, "$.reasoningEffort");
  const timing = asRecord(root.timing, "$.timing");
  const quiet = asRecord(timing.responseCompletionQuietMs, "$.timing.responseCompletionQuietMs");
  const webQuiet = asRecord(quiet.web, "$.timing.responseCompletionQuietMs.web");
  const unityLocalAudioActivity = asRecord(
    timing.unityLocalAudioActivity,
    "$.timing.unityLocalAudioActivity",
  );

  const idleTimeout = turnDetection.idleTimeoutMs;
  if (idleTimeout !== null && (typeof idleTimeout !== "number" || !Number.isFinite(idleTimeout))) {
    throw new Error("$.turnDetection.idleTimeoutMs must be a finite number or null");
  }

  return {
    version: 1,
    devices,
    turnDetection: {
      prefixPaddingMs: asNonNegativeNumber(
        turnDetection.prefixPaddingMs,
        "$.turnDetection.prefixPaddingMs",
      ),
      silenceDurationMs: asNonNegativeNumber(
        turnDetection.silenceDurationMs,
        "$.turnDetection.silenceDurationMs",
      ),
      interruptResponse: asBoolean(
        turnDetection.interruptResponse,
        "$.turnDetection.interruptResponse",
      ),
      idleTimeoutMs: idleTimeout,
    },
    transcription: {
      model: asNonEmptyString(transcription.model, "$.transcription.model"),
      language: asNonEmptyString(transcription.language, "$.transcription.language"),
    },
    realtime: {
      model: asNonEmptyString(realtime.model, "$.realtime.model"),
      parallelToolCalls: asBoolean(realtime.parallelToolCalls, "$.realtime.parallelToolCalls"),
    },
    reasoningEffort: {
      web: parseReasoningPolicy(reasoning.web, "$.reasoningEffort.web"),
      unity: parseReasoningPolicy(reasoning.unity, "$.reasoningEffort.unity"),
    },
    timing: {
      scriptedStepPostSpeechWaitMs: asNonNegativeNumber(
        timing.scriptedStepPostSpeechWaitMs,
        "$.timing.scriptedStepPostSpeechWaitMs",
      ),
      webScriptedStepFallbackPostSpeechWaitMs: asNonNegativeNumber(
        timing.webScriptedStepFallbackPostSpeechWaitMs,
        "$.timing.webScriptedStepFallbackPostSpeechWaitMs",
      ),
      responseCompletionQuietMs: {
        web: {
          afterOutputBufferStoppedMs: asNonNegativeNumber(
            webQuiet.afterOutputBufferStoppedMs,
            "$.timing.responseCompletionQuietMs.web.afterOutputBufferStoppedMs",
          ),
          withoutOutputBufferEventsMs: asNonNegativeNumber(
            webQuiet.withoutOutputBufferEventsMs,
            "$.timing.responseCompletionQuietMs.web.withoutOutputBufferEventsMs",
          ),
        },
        unity: asNonNegativeNumber(
          quiet.unity,
          "$.timing.responseCompletionQuietMs.unity",
        ),
      },
      unityLocalAudioActivity: {
        rmsThreshold: asUnitIntervalNumber(
          unityLocalAudioActivity.rmsThreshold,
          "$.timing.unityLocalAudioActivity.rmsThreshold",
        ),
        agentSpeakingDrainCeilingMs: asNonNegativeNumber(
          unityLocalAudioActivity.agentSpeakingDrainCeilingMs,
          "$.timing.unityLocalAudioActivity.agentSpeakingDrainCeilingMs",
        ),
        userSpeakingHangoverMs: asNonNegativeNumber(
          unityLocalAudioActivity.userSpeakingHangoverMs,
          "$.timing.unityLocalAudioActivity.userSpeakingHangoverMs",
        ),
      },
      tapToListenNoSpeechTimeoutMs: asNonNegativeNumber(
        timing.tapToListenNoSpeechTimeoutMs,
        "$.timing.tapToListenNoSpeechTimeoutMs",
      ),
      headsetPlaybackLeadMs: asNonNegativeNumber(
        timing.headsetPlaybackLeadMs,
        "$.timing.headsetPlaybackLeadMs",
      ),
    },
  };
}

async function loadVoiceSettingsAsync(): Promise<VoiceSettings> {
  const response = await fetch(K_SETTINGS_PATH, { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Failed to read ${K_SETTINGS_PATH}: HTTP ${response.status}`);
  }
  const settings = parseVoiceSettings(await response.json());
  s_settings = settings;
  return settings;
}

function parseReasoningPolicy(value: unknown, path: string): VoiceReasoningPolicy {
  const raw = asRecord(value, path);
  const rolesRaw = asRecord(raw.roles, `${path}.roles`);
  const roles: Record<string, VoiceReasoningEffort> = {};
  for (const [rawRoleId, effortValue] of Object.entries(rolesRaw)) {
    const roleId = rawRoleId.trim().toLowerCase();
    if (!roleId) throw new Error(`${path}.roles contains a blank role id`);
    roles[roleId] = asReasoningEffort(effortValue, `${path}.roles.${rawRoleId}`);
  }
  return {
    default: asReasoningEffort(raw.default, `${path}.default`),
    roles,
  };
}

function asRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, path: string): string {
  if (typeof value !== "string") throw new Error(`${path} must be a string`);
  return value;
}

function asNonEmptyString(value: unknown, path: string): string {
  const text = asString(value, path).trim();
  if (!text) throw new Error(`${path} must not be empty`);
  return text;
}

function asNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number`);
  }
  return value;
}

function asNonNegativeNumber(value: unknown, path: string): number {
  const number = asNumber(value, path);
  if (number < 0) throw new Error(`${path} must be non-negative`);
  return number;
}

function asUnitIntervalNumber(value: unknown, path: string): number {
  const number = asNumber(value, path);
  if (number < 0 || number > 1) throw new Error(`${path} must be between 0 and 1`);
  return number;
}

function asBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function asReasoningEffort(value: unknown, path: string): VoiceReasoningEffort {
  const effort = asString(value, path);
  if (!(K_REASONING_EFFORTS as ReadonlyArray<string>).includes(effort)) {
    throw new Error(`${path} is not a supported reasoning effort`);
  }
  return effort as VoiceReasoningEffort;
}
